import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminTherapistApi,
  TherapistListParams,
  TherapistStatus,
} from '../../api/adminTherapistApi';

// Query keys for cache management
export const therapistQueryKeys = {
  all: ['admin', 'therapists'] as const,
  lists: () => [...therapistQueryKeys.all, 'list'] as const,
  list: (params: TherapistListParams) => [...therapistQueryKeys.lists(), params] as const,
  details: () => [...therapistQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...therapistQueryKeys.details(), id] as const,
  statusCounts: () => [...therapistQueryKeys.all, 'counts'] as const,
  history: (id: string) => [...therapistQueryKeys.all, 'history', id] as const,
};

/**
 * Hook to fetch paginated list of therapists with filtering
 */
export function useTherapistList(params: TherapistListParams = {}) {
  return useQuery({
    queryKey: therapistQueryKeys.list(params),
    queryFn: () => adminTherapistApi.listTherapists(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single therapist by ID
 */
export function useTherapist(id: string) {
  return useQuery({
    queryKey: therapistQueryKeys.detail(id),
    queryFn: () => adminTherapistApi.getTherapist(id),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch therapist status counts for dashboard
 */
export function useTherapistStatusCounts() {
  return useQuery({
    queryKey: therapistQueryKeys.statusCounts(),
    queryFn: () => adminTherapistApi.getStatusCounts(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch therapist status history
 */
export function useTherapistStatusHistory(id: string, page: number = 1) {
  return useQuery({
    queryKey: [...therapistQueryKeys.history(id), page],
    queryFn: () => adminTherapistApi.getStatusHistory(id, page),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to approve a pending therapist
 */
export function useApproveTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      adminTherapistApi.approveTherapist(id, notes),
    onSuccess: (_, variables) => {
      // Invalidate all therapist-related queries
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.all });
      // Also invalidate the specific therapist detail
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to reject a pending therapist
 */
export function useRejectTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminTherapistApi.rejectTherapist(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to suspend an active therapist
 */
export function useSuspendTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      reason,
      cancelFutureBookings,
    }: {
      id: string;
      reason: string;
      cancelFutureBookings?: boolean;
    }) => adminTherapistApi.suspendTherapist(id, reason, cancelFutureBookings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to reactivate a suspended or inactive therapist
 */
export function useReactivateTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      adminTherapistApi.reactivateTherapist(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: therapistQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Helper to get available actions for a therapist based on their status
 */
export function getAvailableActions(status: TherapistStatus): {
  canApprove: boolean;
  canReject: boolean;
  canSuspend: boolean;
  canReactivate: boolean;
} {
  return {
    canApprove: status === 'PENDING',
    canReject: status === 'PENDING',
    canSuspend: status === 'ACTIVE',
    canReactivate: status === 'SUSPENDED' || status === 'INACTIVE',
  };
}
